import React, { useEffect, useState } from "react";
import { fetchTenantConfigs, updateTenantConfigs } from "../../api/tenantConfigApi";

const DEFAULTS = { enabled: false, ai_label: "AI", ai_icon_enabled: true, human_name_enabled: true };

export default function MessageIdentitySettings({ botId, disabled = false }) {
  const [fields, setFields] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setLoadFailed(false);
    setSaved(false);
    fetchTenantConfigs(botId).then((response) => {
      if (!active) return;
      const configs = response?.data || response;
      if (!Array.isArray(configs)) throw new Error("Invalid bot settings response.");
      setFields({ ...DEFAULTS, ...configs.find((item) => item.config_key === "message_identity")?.config_json });
    }).catch((err) => {
      if (active) {
        setLoadFailed(true);
        setError(err.message || "Unable to load message identity settings.");
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [botId, retry]);

  const update = (key, value) => {
    setSaved(false);
    setFields((current) => ({ ...current, [key]: value }));
  };
  const label = fields.ai_label.trim();
  const invalidLabel = !label || [...label].length > 40 || [...label].some(
    (char) => char.charCodeAt(0) < 32 || "[]\u007f\u2028\u2029".includes(char)
  );
  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const response = await updateTenantConfigs(botId, {
        config_key: "message_identity", config_json: { ...fields, ai_label: label },
      });
      setFields({ ...DEFAULTS, ...(response?.data || response).config_json });
      setSaved(true);
    } catch (err) {
      setError(typeof err.details?.detail === "string" ? err.details.detail : err.message || "Unable to save message identity settings.");
    } finally {
      setSaving(false);
    }
  };
  const locked = disabled || loading || saving;
  const sample = "Hello, how can we help?";
  const aiPreview = fields.enabled ? `${fields.ai_icon_enabled ? "\u{1F916} " : ""}[${label || "AI"}]\n${sample}` : sample;
  const humanPreview = fields.enabled ? `[${fields.human_name_enabled ? "Ajay" : "Team"}]\n${sample}` : sample;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4" aria-label="WhatsApp message identity">
      <h3 className="text-sm font-semibold text-slate-900">WhatsApp message identity</h3>
      <p className="text-xs text-slate-500">Customer-visible prefixes for this business bot, across all recipients. Off by default.</p>
      {loading ? <p role="status">Loading message identity settings...</p> : (
        <fieldset disabled={locked} className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={fields.enabled} onChange={(e) => update("enabled", e.target.checked)} />
            Show sender prefix to customers
          </label>
          <label className="block text-sm">
            AI label
            <input className="ml-3 rounded-lg border border-slate-200 px-3 py-2" value={fields.ai_label}
              maxLength={40} disabled={!fields.enabled} onChange={(e) => update("ai_label", e.target.value)} />
          </label>
          {invalidLabel && <p className="text-xs text-rose-700">Use 1-40 characters on one line, without brackets or control characters.</p>}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={fields.ai_icon_enabled} disabled={!fields.enabled}
              onChange={(e) => update("ai_icon_enabled", e.target.checked)} />
            Show robot icon on AI replies
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={fields.human_name_enabled} disabled={!fields.enabled}
              onChange={(e) => update("human_name_enabled", e.target.checked)} />
            Show staff names on Inbox replies
          </label>
          <p className="text-xs text-slate-500">Staff names come from the signed-in user. Hidden or missing names display Team.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><p className="text-xs font-medium text-slate-600">AI preview</p><pre data-testid="ai-identity-preview" className="whitespace-pre-wrap rounded-xl bg-violet-50 p-3 text-sm">{aiPreview}</pre></div>
            <div><p className="text-xs font-medium text-slate-600">Staff preview (example)</p><pre data-testid="human-identity-preview" className="whitespace-pre-wrap rounded-xl bg-emerald-50 p-3 text-sm">{humanPreview}</pre></div>
          </div>
          <p className="text-xs text-slate-500">Applies to new text and interactive message bodies only. Templates, scheduled sends, attachments (this upload flow has no captions), and WhatsApp Business App messages stay unchanged. Prefixes count toward WhatsApp body limits.</p>
          <button type="button" onClick={save} disabled={locked || invalidLabel || loadFailed}
            className="rounded-xl bg-[#096dea] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
            {saving ? "Saving..." : "Save message identity"}
          </button>
        </fieldset>
      )}
      {error && <div role="alert" className="text-sm text-rose-700">{error} <button type="button" onClick={() => setRetry((value) => value + 1)} disabled={saving}>Reload settings</button></div>}
      {saved && <p role="status" className="text-sm text-emerald-700">Message identity saved.</p>}
    </section>
  );
}
